import { AxiosRequestConfig } from 'axios';
import api from './api';

interface CacheEntry<T> {
  expiresAt: number;
  promise?: Promise<T>;
  value?: T;
}

interface CacheOptions {
  ttlMs?: number;
  force?: boolean;
}

const requestCache = new Map<string, CacheEntry<unknown>>();

const serializeValue = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) {
    return `[${value.map((item) => serializeValue(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));
    return `{${entries.map(([key, entryValue]) => `${key}:${serializeValue(entryValue)}`).join(',')}}`;
  }
  return String(value);
};

const buildCacheKey = (url: string, config?: AxiosRequestConfig): string => {
  const paramsKey = config?.params ? serializeValue(config.params) : '';
  return `${url}?${paramsKey}`;
};

export const fetchCached = async <T>(
  url: string,
  config?: AxiosRequestConfig,
  options?: CacheOptions,
): Promise<T> => {
  const ttlMs = options?.ttlMs ?? 60_000;
  const force = options?.force ?? false;
  const cacheKey = buildCacheKey(url, config);
  const now = Date.now();
  const cachedEntry = requestCache.get(cacheKey) as CacheEntry<T> | undefined;

  if (!force && cachedEntry && cachedEntry.expiresAt > now) {
    if (cachedEntry.value !== undefined) {
      return cachedEntry.value;
    }
    if (cachedEntry.promise) {
      return cachedEntry.promise;
    }
  }

  const requestPromise = api.get<T>(url, config).then((response) => response.data);
  requestCache.set(cacheKey, {
    expiresAt: now + ttlMs,
    promise: requestPromise,
  });

  try {
    const value = await requestPromise;
    requestCache.set(cacheKey, {
      expiresAt: Date.now() + ttlMs,
      value,
    });
    return value;
  } catch (error) {
    requestCache.delete(cacheKey);
    throw error;
  }
};

export const invalidateCachedRequests = (matcher?: string | ((cacheKey: string) => boolean)) => {
  if (!matcher) {
    requestCache.clear();
    return;
  }

  requestCache.forEach((_entry, cacheKey) => {
    const shouldDelete =
      typeof matcher === 'string' ? cacheKey.startsWith(matcher) : matcher(cacheKey);
    if (shouldDelete) {
      requestCache.delete(cacheKey);
    }
  });
};
